import { VIMEO_REG_EXP } from '../../constants';

/** Only Vimeo-owned player/share hosts qualify for Vimeo playback policy. */
export function isVimeoVideoUrl(value: string): boolean {
  try {
    const url = new URL(value.startsWith('//') ? `https:${value}` : value);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(url.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * Extracts the video ID from various formats of Vimeo URLs.
 *
 * Supports extracting IDs from:
 * - Standard Vimeo URLs (`https://vimeo.com/123456789`)
 * - Vimeo Player URLs (`https://player.vimeo.com/video/123456789`)
 * - Vimeo Channels URLs (`https://vimeo.com/channels/staffpicks/123456789`)
 * - Vimeo Groups URLs (`https://vimeo.com/groups/name/videos/123456789`)
 *
 * @param url - The Vimeo URL to parse. Can include query parameters or hash.
 * @returns The extracted video ID as a string, or `null` if the URL is invalid or not a Vimeo URL.
 *
 * @example
 * getIdFromVimeoUrl('https://vimeo.com/123456789'); // Returns '123456789'
 * getIdFromVimeoUrl('https://player.vimeo.com/video/987654321'); // Returns '987654321'
 * getIdFromVimeoUrl('https://youtube.com/watch?v=123456789'); // Returns null
 * getIdFromVimeoUrl('https://vimeo.com/notanid'); // Returns null
 */
export const getIdFromVimeoUrl = (url: string): string | null => {
  if (!isVimeoVideoUrl(url)) return null;
  const result = new URL(url.startsWith('//') ? `https:${url}` : url).href.match(VIMEO_REG_EXP);
  return result?.[4] ?? null;
};

const VIMEO_API_URI_RE = /^\/videos\/(\d+)(?:\?h=[A-Za-z0-9_-]+)?$/;

/** Extracts the numeric id from a Vimeo API URI (`/videos/<id>`). */
export const getIdFromVimeoApiUri = (path: string): string | null => {
  const match = VIMEO_API_URI_RE.exec(path);
  return match?.[1] ?? null;
};

/** `?h=<hash>` on player embed URLs, `/<id>/<hash>` on share URLs. */
const VIMEO_PRIVACY_HASH_QUERY_RE = /[?&]h=([A-Za-z0-9_-]+)/;
const VIMEO_PRIVACY_HASH_PATH_RE = /vimeo\.com\/(?:video\/)?\d+\/([A-Za-z0-9_-]+)/;

/**
 * Extracts the privacy hash that Vimeo requires for `unlisted` videos.
 *
 * Vimeo hands the same hash out in two shapes, and both occur in stored data:
 * `https://player.vimeo.com/video/<id>?h=<hash>` and
 * `https://vimeo.com/<id>/<hash>`. Dropping it makes the video unplayable and
 * makes the oEmbed lookup return 404, because an unlisted video is not
 * addressable by id alone.
 *
 * @returns The hash, or `null` when the URL carries none (public videos, and
 * videos whose privacy is `disable`, do not need one).
 *
 * @example
 * getPrivacyHashFromVimeoUrl('https://player.vimeo.com/video/123?h=abc'); // 'abc'
 * getPrivacyHashFromVimeoUrl('https://vimeo.com/123/abc?share=copy'); // 'abc'
 * getPrivacyHashFromVimeoUrl('https://vimeo.com/123'); // null
 */
export const getPrivacyHashFromVimeoUrl = (url: string): string | null =>
  VIMEO_PRIVACY_HASH_QUERY_RE.exec(url)?.[1] ?? VIMEO_PRIVACY_HASH_PATH_RE.exec(url)?.[1] ?? null;

/** Canonical Vimeo resource path stored for `StorageType.Vimeo`. */
export const toVimeoResourcePath = (vimeoId: string, privacyHash?: string | null): string =>
  privacyHash ? `/videos/${vimeoId}?h=${privacyHash}` : `/videos/${vimeoId}`;

/**
 * Share URL ReactPlayer (and admins) understand.
 *
 * `privacyHash` must be passed through for `unlisted` videos: both the Vimeo
 * player element and the oEmbed API reject an id-only URL for those.
 */
export const toPlayableVimeoUrl = (vimeoId: string, privacyHash?: string | null): string =>
  privacyHash ? `https://vimeo.com/${vimeoId}/${privacyHash}` : `https://vimeo.com/${vimeoId}`;

/**
 * Converts a stored video resource path into a playable URL for the UI.
 * Vimeo resources are stored as `/videos/<id>` but the player needs a share URL.
 * Any privacy hash present in the stored path is carried over.
 */
export const toPlayableVideoUrl = (relativePath: string): string => {
  const vimeoId = getIdFromVimeoApiUri(relativePath) ?? getIdFromVimeoUrl(relativePath);
  if (vimeoId) {
    return toPlayableVimeoUrl(vimeoId, getPrivacyHashFromVimeoUrl(relativePath));
  }
  return relativePath;
};

/**
 * True when `value` is a usable video URL for admin edit / Network video storage:
 * - http(s) only (rejects whitespace-only, malformed, and non-http schemes)
 * - Vimeo hosts must include a parseable numeric video id
 */
export function isAllowedVideoUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }

  if (parsed.hostname.toLowerCase().includes('vimeo.com')) {
    return getIdFromVimeoUrl(trimmed) !== null;
  }

  return true;
}

/** Only YouTube-owned watch/share/embed hosts qualify for a YouTube embed. */
const YOUTUBE_HOSTS: ReadonlySet<string> = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'www.youtu.be',
]);

/** A YouTube id is 11 characters of its own alphabet, and nothing else. */
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_PATH_ID_RE = /^\/(?:embed|shorts|v|live)\/([A-Za-z0-9_-]{11})(?:\/|$)/;

function parseHttpUrl(value: string): URL | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;

  try {
    const url = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : undefined;
  } catch {
    return undefined;
  }
}

/** Only YouTube-owned hosts qualify for YouTube playback policy. */
export function isYouTubeVideoUrl(value: string): boolean {
  const url = parseHttpUrl(value);
  return url !== undefined && YOUTUBE_HOSTS.has(url.hostname.toLowerCase());
}

/**
 * Extracts the video id from the YouTube URL shapes people paste.
 *
 * `https://www.youtube.com/watch?v=<id>`, `https://youtu.be/<id>`,
 * `https://www.youtube.com/embed/<id>`, `/shorts/<id>`, `/v/<id>` and
 * `/live/<id>`, with or without query parameters.
 *
 * @returns the id, or `null` when the host is not YouTube's or the path
 * carries no id of the expected shape — a channel, a playlist or a search
 * result is not a video.
 */
export const getIdFromYouTubeUrl = (value: string): string | null => {
  const url = parseHttpUrl(value);
  if (!(url && YOUTUBE_HOSTS.has(url.hostname.toLowerCase()))) return null;

  if (url.hostname.toLowerCase().endsWith('youtu.be')) {
    const shortId = url.pathname.slice(1).split('/')[0] ?? '';
    return YOUTUBE_ID_RE.test(shortId) ? shortId : null;
  }

  const watchId = url.searchParams.get('v');
  if (watchId !== null) return YOUTUBE_ID_RE.test(watchId) ? watchId : null;

  return YOUTUBE_PATH_ID_RE.exec(url.pathname)?.[1] ?? null;
};

/** Share URL the player understands, built from the id alone. */
export const toPlayableYouTubeUrl = (youTubeId: string): string => `https://www.youtube.com/watch?v=${youTubeId}`;

/** The providers a post body may embed a player for. */
export const EMBEDDABLE_VIDEO_PROVIDERS = ['VIMEO', 'YOUTUBE'] as const;
export type EmbeddableVideoProvider = (typeof EMBEDDABLE_VIDEO_PROVIDERS)[number];

export interface EmbeddableVideo {
  provider: EmbeddableVideoProvider;
  /** The provider's own id, for a stable key and for assertions. */
  id: string;
  /** The URL to hand the player, rebuilt from the id rather than passed along. */
  url: string;
}

/**
 * The embeddable video a URL names, or `undefined` for anything else.
 *
 * This is the allowlist for turning a link inside a body into a player, and it
 * is deliberately narrower than `isAllowedVideoUrl`, which admits any http(s)
 * host for an administrator's own video record. Here the URL came from whoever
 * wrote the post, so only the two providers the product embeds qualify, only
 * when the host is theirs and the path carries a real video id.
 *
 * The URL handed to the player is rebuilt from the parsed id (and Vimeo's
 * privacy hash, without which an unlisted video will not play), so nothing
 * else a link carried — a query string, a fragment, credentials — reaches the
 * provider.
 */
export function resolveEmbeddableVideo(value: string): EmbeddableVideo | undefined {
  const vimeoId = getIdFromVimeoUrl(value);
  if (vimeoId) {
    return {
      provider: 'VIMEO',
      id: vimeoId,
      url: toPlayableVimeoUrl(vimeoId, getPrivacyHashFromVimeoUrl(value)),
    };
  }

  const youTubeId = getIdFromYouTubeUrl(value);
  if (youTubeId) {
    return { provider: 'YOUTUBE', id: youTubeId, url: toPlayableYouTubeUrl(youTubeId) };
  }

  return undefined;
}

function readPositiveDurationSeconds(data: unknown): number | undefined {
  if (typeof data !== 'object' || data === null || !('duration' in data)) {
    return undefined;
  }

  const { duration } = data;
  if (typeof duration !== 'number' || !Number.isFinite(duration) || duration <= 0) {
    return undefined;
  }

  return Math.round(duration);
}

/**
 * Fetches duration (seconds) for a Vimeo URL via the public oEmbed API.
 * Returns undefined for non-Vimeo URLs or when the lookup fails.
 */
export async function fetchVimeoVideoDurationSeconds(url: string): Promise<number | undefined> {
  const vimeoId = getIdFromVimeoUrl(url) ?? getIdFromVimeoApiUri(url);
  if (!vimeoId) {
    return undefined;
  }

  try {
    const playableUrl = toPlayableVimeoUrl(vimeoId, getPrivacyHashFromVimeoUrl(url));
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(playableUrl)}`;
    const response = await fetch(oembedUrl);
    if (!response.ok) {
      return undefined;
    }

    return readPositiveDurationSeconds(await response.json());
  } catch {
    return undefined;
  }
}
